import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useRealtime } from "@/lib/realtime";
import MapView from "@/components/MapView";
import MobileBrandHeader from "@/components/MobileBrandHeader";
import {
  Power, Star, Clock, MapPin, Phone, Navigation as NavIcon,
  CheckCircle2, X, Loader2, ShieldAlert, Car, Users, Sparkles, ArrowLeft
} from "lucide-react";

const ABU_DHABI = [24.4539, 54.3773];

export default function DriverHome() {
  const { user, refresh } = useAuth();
  const [online, setOnline] = useState(user?.online || false);
  const [stats, setStats] = useState(null);
  const [incoming, setIncoming] = useState(null);
  const [active, setActive] = useState(null);
  const [countdown, setCountdown] = useState(30);
  const [showNav, setShowNav] = useState(false);

  const loc = user?.current_lat ? [user.current_lat, user.current_lng] : ABU_DHABI;

  const poll = useCallback(async () => {
    try {
      const { data: a } = await api.get("/driver/active-trip");
      setActive(a);
      if (!a) {
        const { data: i } = await api.get("/driver/incoming");
        setIncoming(i);
      } else {
        setIncoming(null);
      }
      const { data: s } = await api.get("/driver/stats");
      setStats(s);
    } catch (e) {
      // ignore — will retry on next poll
    }
  }, []);

  useEffect(() => {
    poll();
    const t = setInterval(poll, 4000);
    return () => clearInterval(t);
  }, [poll]);

  useRealtime((msg) => {
    if (msg.type === "notification" || msg.type === "driver_location") {
      poll();
    }
  });

  // Countdown for incoming
  useEffect(() => {
    if (!incoming) return;
    if (countdown <= 0) {
      decline(incoming.id);
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [incoming, countdown]);

  // Reset countdown when a new incoming arrives
  useEffect(() => {
    if (incoming) setCountdown(30);
  }, [incoming?.id]);

  async function toggleOnline() {
    try {
      const { data } = await api.post("/driver/status", {
        online: !online,
        lat: loc[0],
        lng: loc[1],
      });
      setOnline(data.online);
      refresh();
      toast.success(data.online ? "You're online — looking for rides" : "You're offline");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  async function accept(tid) {
    try {
      await api.post(`/driver/trips/${tid}/accept`);
      toast.success("Ride accepted");
      poll();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  async function decline(tid) {
    try {
      await api.post(`/driver/trips/${tid}/decline`);
      toast("Ride declined");
      poll();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }

  async function arrive() {
    await api.post(`/driver/trips/${active.id}/arrive`);
    poll();
  }
  async function start() {
    await api.post(`/driver/trips/${active.id}/start`);
    poll();
  }
  async function complete() {
    await api.post(`/driver/trips/${active.id}/complete`);
    toast.success("Trip completed");
    poll();
  }

  // ---------------- ACTIVE TRIP VIEW ----------------
  if (active) {
    const passenger = active.passengers?.[0];
    const booking = passenger?.booking;
    const isEnRoute = active.status === "en_route_pickup";
    const isAtPickup = active.status === "at_pickup";
    const isInProgress = active.status === "in_progress";

    const dest = isInProgress
      ? { lat: booking?.dropoff_lat, lng: booking?.dropoff_lng, label: booking?.dropoff_address }
      : { lat: booking?.pickup_lat, lng: booking?.pickup_lng, label: booking?.pickup_address };

    // distance + bearing for in-app nav
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = (dest.lat || 0) - loc[0];
    const dLng = (dest.lng || 0) - loc[1];
    const distKm = Math.sqrt(dLat * dLat + dLng * dLng) * 111;
    const brg = (Math.atan2(Math.sin(toRad(dLng)) * Math.cos(toRad(dest.lat || 0)), Math.cos(toRad(loc[0])) * Math.sin(toRad(dest.lat || 0)) - Math.sin(toRad(loc[0])) * Math.cos(toRad(dest.lat || 0)) * Math.cos(toRad(dLng))) * 180) / Math.PI;
    const compass = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
    const bearing = compass[Math.round((((brg + 360) % 360) / 45)) % 8];

    return (
      <div className="relative min-h-screen bg-slate-50">
        <div className="absolute inset-0 z-0 h-[60vh]">
          <MapView
            pickup={{ lat: booking?.pickup_lat, lng: booking?.pickup_lng }}
            dropoff={{ lat: booking?.dropoff_lat, lng: booking?.dropoff_lng }}
            driver={{ lat: loc[0], lng: loc[1] }}
            route={[loc, [dest.lat, dest.lng]]}
            center={[(loc[0] + dest.lat) / 2, (loc[1] + dest.lng) / 2]}
            zoom={11}
          />
        </div>

        {showNav && (
          <div className="fixed inset-0 z-50 bg-white sheet-enter flex flex-col">
            <div className="px-4 pt-safe pt-4 pb-3 border-b border-slate-100 flex items-center gap-3">
              <button onClick={() => setShowNav(false)} className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center" data-testid="nav-close">
                <ArrowLeft size={20} />
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">In-app navigation</p>
                <p className="font-display font-bold text-slate-900 truncate">{dest.label}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Distance</p>
                <p className="font-display font-bold text-primary">{distKm.toFixed(1)} km</p>
              </div>
            </div>
            <div className="flex-1 relative">
              <MapView
                pickup={{ lat: booking?.pickup_lat, lng: booking?.pickup_lng }}
                dropoff={{ lat: booking?.dropoff_lat, lng: booking?.dropoff_lng }}
                driver={{ lat: loc[0], lng: loc[1] }}
                route={[loc, [dest.lat, dest.lng]]}
                center={[(loc[0] + dest.lat) / 2, (loc[1] + dest.lng) / 2]}
                zoom={12}
              />
              <div className="absolute top-4 left-4 right-4 bg-slate-900 text-white rounded-2xl px-4 py-3 flex items-center gap-3 shadow-xl">
                <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center">
                  <NavIcon size={20} />
                </div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-widest text-primary font-bold">Heading {bearing}</p>
                  <p className="font-display font-bold">Follow route on map</p>
                </div>
                <span className="text-xs font-mono">~{Math.max(1, Math.round(distKm * 1.8))} min</span>
              </div>
            </div>
            <div className="p-4 pb-safe bg-white border-t border-slate-100 grid grid-cols-2 gap-2">
              {isEnRoute && (
                <button onClick={() => { arrive(); setShowNav(false); }} className="h-12 rounded-xl bg-primary text-white font-semibold flex items-center justify-center gap-2 col-span-2">
                  <CheckCircle2 size={18} /> I&apos;ve Arrived
                </button>
              )}
              {isAtPickup && (
                <button onClick={() => { start(); setShowNav(false); }} className="h-12 rounded-xl bg-primary text-white font-semibold flex items-center justify-center gap-2 col-span-2">
                  <Sparkles size={18} /> Start Ride
                </button>
              )}
              {isInProgress && (
                <button onClick={() => { complete(); setShowNav(false); }} className="h-12 rounded-xl bg-emerald-500 text-white font-semibold flex items-center justify-center gap-2 col-span-2">
                  <CheckCircle2 size={18} /> Complete Ride
                </button>
              )}
            </div>
          </div>
        )}

        <div className="absolute top-0 left-0 right-0 z-10">
          <MobileBrandHeader testId="driver-brand-header" rightSlot={<span className="text-[10px] uppercase tracking-widest text-primary font-bold bg-white/90 px-2 py-1 rounded-full">Driver</span>} />
          <div className="px-4 pb-3 flex items-center justify-between">
            <div className="bg-white px-3 py-2 rounded-full shadow flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                {active.status.replace(/_/g, " ")}
              </span>
            </div>
            <button className="w-11 h-11 rounded-full bg-rose-50 text-rose-600 shadow flex items-center justify-center" data-testid="driver-sos">
              <ShieldAlert size={18} />
            </button>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-10 sheet-enter">
          <div className="bg-white rounded-t-[28px] shadow-2xl px-5 pt-5 pb-safe">
            <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mb-4" />

            <p className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">
              {isInProgress ? "Drop off at" : "Pick up at"}
            </p>
            <h2 className="font-display font-bold text-xl text-slate-900 mt-1" data-testid="active-destination">{dest.label}</h2>

            <div className="mt-4 flex items-center gap-3 p-3 rounded-2xl bg-slate-50">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primary-700 text-white flex items-center justify-center font-display font-bold">
                {passenger?.name?.charAt(0)}
              </div>
              <div className="flex-1">
                <p className="font-display font-bold text-slate-900">{passenger?.name}</p>
                <p className="text-xs text-slate-500 font-mono">{passenger?.enec_id}</p>
              </div>
              <a href={`tel:${passenger?.phone}`} className="w-11 h-11 rounded-full bg-white border border-slate-200 text-slate-700 flex items-center justify-center" data-testid="call-passenger">
                <Phone size={18} />
              </a>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                onClick={() => setShowNav(true)}
                data-testid="navigate-btn"
                className="h-12 rounded-xl bg-slate-900 text-white font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform"
              >
                <NavIcon size={18} /> Navigate
              </button>
              {isEnRoute && (
                <button data-testid="i-arrived" onClick={arrive} className="h-12 rounded-xl bg-primary text-white font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform">
                  <CheckCircle2 size={18} /> I&apos;ve Arrived
                </button>
              )}
              {isAtPickup && (
                <button data-testid="start-ride" onClick={start} className="h-12 rounded-xl bg-primary text-white font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform">
                  <Sparkles size={18} /> Start Ride
                </button>
              )}
              {isInProgress && (
                <button data-testid="complete-ride" onClick={complete} className="h-12 rounded-xl bg-emerald-500 text-white font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform">
                  <CheckCircle2 size={18} /> Complete
                </button>
              )}
            </div>

            <p className="text-center text-[10px] font-mono text-slate-400 mt-3">TRIP · {active.ref}</p>
          </div>
        </div>
      </div>
    );
  }

  // ---------------- INCOMING REQUEST MODAL ----------------
  if (incoming) {
    const passenger = incoming.passengers?.[0];
    const booking = passenger?.booking;
    return (
      <div className="min-h-screen bg-slate-100 relative">
        <div className="absolute inset-0 z-0">
          <MapView
            pickup={{ lat: booking?.pickup_lat, lng: booking?.pickup_lng }}
            dropoff={{ lat: booking?.dropoff_lat, lng: booking?.dropoff_lng }}
            route={[[booking?.pickup_lat, booking?.pickup_lng], [booking?.dropoff_lat, booking?.dropoff_lng]]}
            center={[booking?.pickup_lat, booking?.pickup_lng]}
            zoom={11}
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10 pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 z-20">
          <MobileBrandHeader testId="driver-brand-header" rightSlot={<span className="text-[10px] uppercase tracking-widest text-primary font-bold bg-white/90 px-2 py-1 rounded-full">Driver</span>} />
        </div>
        <div className="absolute bottom-0 left-0 right-0 z-20 sheet-enter">
          <div className="bg-white rounded-t-[28px] shadow-2xl px-5 pt-5 pb-safe">
            <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mb-3" />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-widest text-primary font-bold">New ride request</p>
                <h2 className="font-display font-bold text-2xl text-slate-900">{passenger?.name}</h2>
              </div>
              <div className="relative w-14 h-14">
                <svg className="w-14 h-14 -rotate-90" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="16" stroke="#E2E8F0" strokeWidth="2.5" fill="none" />
                  <circle cx="18" cy="18" r="16" stroke="#22B8E0" strokeWidth="2.5" fill="none" strokeDasharray={`${(countdown / 30) * 100} 100`} strokeLinecap="round" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-sm font-display font-bold text-slate-900">{countdown}</span>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                <div className="w-2 h-2 rounded-full bg-slate-900 ring-4 ring-slate-200 mt-1.5" />
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Pickup</p>
                  <p className="font-semibold text-slate-900 text-sm">{booking?.pickup_address}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl">
                <div className="w-2 h-2 rounded-full bg-primary ring-4 ring-primary-200 mt-1.5" />
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Drop-off</p>
                  <p className="font-semibold text-slate-900 text-sm">{booking?.dropoff_address}</p>
                </div>
              </div>
            </div>

            <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
              <span className="flex items-center gap-1"><Users size={13} /> {booking?.seats_requested || 1} seats</span>
              <span className="flex items-center gap-1"><Car size={13} /> {booking?.vehicle_preference || "standard"}</span>
              <span className="px-2 py-0.5 rounded-full bg-primary-50 text-primary font-bold uppercase tracking-wide text-[10px]">
                {incoming.ref}
              </span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <button onClick={() => decline(incoming.id)} data-testid="decline-trip" className="h-14 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-display font-bold text-lg flex items-center justify-center gap-2">
                <X size={20} /> Decline
              </button>
              <button onClick={() => accept(incoming.id)} data-testid="accept-trip" className="h-14 rounded-xl bg-primary hover:bg-primary-600 text-white font-display font-bold text-lg flex items-center justify-center gap-2">
                <CheckCircle2 size={20} /> Accept
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------------- IDLE / ONLINE HOME ----------------
  return (
    <div className="relative min-h-screen bg-slate-50">
      <div className="absolute inset-0 z-0 opacity-90">
        <MapView userLocation={loc} center={loc} zoom={12} />
      </div>

      <div className="absolute inset-0 z-10 flex flex-col">
        <MobileBrandHeader testId="driver-brand-header" rightSlot={<span className="text-[10px] uppercase tracking-widest text-primary font-bold bg-white/80 px-2 py-1 rounded-full">Driver</span>} />
        <div className="px-5 mt-2">
          <div className="glass rounded-2xl px-4 py-3 shadow-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary to-primary-700 text-white flex items-center justify-center font-display font-bold">
                {user?.name?.charAt(0)}
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Driver</p>
                <p className="font-display font-bold text-slate-900">{user?.name}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 justify-end">
                <Star size={14} className="fill-amber-400 text-amber-400" />
                <span className="font-display font-bold text-slate-900">{user?.rating || "5.0"}</span>
              </div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Rating</p>
            </div>
          </div>

          {stats && (
            <div className="mt-3 grid grid-cols-3 gap-2">
              <div className="bg-white rounded-xl p-3 border border-slate-100">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Trips</p>
                <p className="font-display font-bold text-xl text-slate-900" data-testid="stat-trips-today">{stats.trips_today}</p>
              </div>
              <div className="bg-white rounded-xl p-3 border border-slate-100">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">KM today</p>
                <p className="font-display font-bold text-xl text-slate-900">{stats.km_today}</p>
              </div>
              <div className="bg-white rounded-xl p-3 border border-slate-100">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Passengers</p>
                <p className="font-display font-bold text-xl text-primary" data-testid="stat-passengers-today">{stats.passengers_today ?? 0}</p>
              </div>
            </div>
          )}
        </div>

        <div className="mt-auto px-5 pb-28">
          <div className="bg-white rounded-3xl shadow-2xl p-6 text-center">
            {online ? (
              <>
                <div className="relative mx-auto w-24 h-24 mb-3">
                  <div className="absolute inset-0 rounded-full bg-primary/15 animate-ping" />
                  <div className="relative w-24 h-24 rounded-full bg-primary text-white flex items-center justify-center mx-auto">
                    <Power size={36} />
                  </div>
                </div>
                <p className="font-display font-bold text-xl text-slate-900">You&apos;re online</p>
                <p className="text-sm text-slate-500">Waiting for ride requests…</p>
                <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mt-2 dot-loading">Listening</p>
              </>
            ) : (
              <>
                <div className="w-24 h-24 mx-auto rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mb-3">
                  <Power size={36} />
                </div>
                <p className="font-display font-bold text-xl text-slate-900">You&apos;re offline</p>
                <p className="text-sm text-slate-500">Go online to start receiving rides.</p>
              </>
            )}

            <button
              data-testid="driver-online-toggle"
              onClick={toggleOnline}
              className={`mt-5 w-full h-14 rounded-xl font-display font-bold text-lg flex items-center justify-center gap-2 transition-colors ${
                online ? "bg-slate-900 hover:bg-slate-800 text-white" : "bg-primary hover:bg-primary-600 text-white"
              }`}
            >
              {online ? "Go Offline" : "Go Online"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
